import { Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import Multiselect from "multiselect-react-dropdown";
import { handleFormChange } from "../../components/modals/EditableTable";
import { isValidPhone } from "../../components/utils/ValidatePhone";
import { PasswordConfirmField, PasswordWithSuggestion } from "./PasswordField";
import UserAccessNotificationMatrix from "./UserAccessNotificationMatrix";
import NotificationTestPanel from "./NotificationTestPanel";
import UserAccessPushDiagnostics from "./UserAccessPushDiagnostics";
import UserAccessPushDeviceStatus from "./UserAccessPushDeviceStatus";
import InfoTip from "../../components/InfoTip";
import { ActivityMetricInfoIcon } from "../../components/ActivityDetailMetricIcons";
import WasteFlowFormPanel from "../settings/waste/WasteFlowFormPanel";
import {
  WASTE_FORM_LABEL_CLASS,
  WASTE_FORM_REQUIRED,
} from "../settings/waste/wasteFlowFormUi";
import {
  USER_ACCESS_HINT,
  USER_ACCESS_INPUT_CLASS,
  USER_ACCESS_SUBCARD_TITLE,
} from "./userAccessFormUi";

const InfoHint = ({ children }) => (
  <p className={USER_ACCESS_HINT}>
    <i
      className="fas fa-info-circle text-[#828899] mt-0.5 shrink-0"
      aria-hidden="true"
    />
    <span>{children}</span>
  </p>
);

const ProfileFieldLabel = ({ htmlFor, children, required, infoTip }) => (
  <label className={`${WASTE_FORM_LABEL_CLASS} gap-1`} htmlFor={htmlFor}>
    <span>{children}</span>
    {required ? WASTE_FORM_REQUIRED : null}
    {infoTip ? (
      <InfoTip nudgeY={0} icon={<ActivityMetricInfoIcon />}>
        {infoTip}
      </InfoTip>
    ) : null}
  </label>
);

export const UserAccessGeneralSection = ({
  data,
  setData,
  isCreate = true,
  googleSSORequired = false,
  setErrorForm,
  passwordResetKey,
}) => {
  const { t } = useTranslation();

  const handleApplyPasswordSuggestion = (generated) => {
    setData((prev) => ({
      ...(prev || {}),
      password: generated,
      passwordConfirm: generated,
    }));
    setErrorForm("");
  };

  return (
    <WasteFlowFormPanel title={t("settingsUsers.stepProfile")}>
      <div className="waste-flow-form-field">
        <ProfileFieldLabel htmlFor="user-first-name">
          {t("First name")}
        </ProfileFieldLabel>
        <Input
          id="user-first-name"
          className={USER_ACCESS_INPUT_CLASS}
          type="text"
          value={data.firstName || ""}
          name="firstName"
          onChange={(e) => handleFormChange(e, data, setData)}
        />
      </div>

      <div className="waste-flow-form-field">
        <ProfileFieldLabel htmlFor="user-last-name">
          {t("Name")}
        </ProfileFieldLabel>
        <Input
          id="user-last-name"
          className={USER_ACCESS_INPUT_CLASS}
          type="text"
          value={data.lastName || ""}
          name="lastName"
          onChange={(e) => handleFormChange(e, data, setData)}
        />
      </div>

      <div className="waste-flow-form-field">
        <ProfileFieldLabel
          htmlFor="user-email"
          required={isCreate}
          infoTip={t("settingsUsers.emailSpamTip")}
        >
          {t("Email address")}
        </ProfileFieldLabel>
        <Input
          id="user-email"
          className={USER_ACCESS_INPUT_CLASS}
          type="email"
          name="email"
          autoComplete={isCreate ? "username" : "email"}
          disabled={!isCreate}
          value={data.email || ""}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/\s/g, "");
            setData((prev) => ({ ...(prev || {}), email: cleaned }));
          }}
        />
      </div>

      <div className="waste-flow-form-field">
        <ProfileFieldLabel htmlFor="user-phone">{t("Phone")}</ProfileFieldLabel>
        <div className="user-access-wizard-phone">
          <PhoneInput
            id="user-phone"
            placeholder={t("Enter phone number")}
            defaultCountry="FR"
            value={
              typeof data.phone === "string"
                ? data.phone
                : data.phone
                  ? `${data.phone}`
                  : ""
            }
            onChange={(number) => {
              const value = number || "";
              if (isValidPhone(value, setErrorForm)) {
                setErrorForm("");
                setData((prev) => ({ ...(prev || {}), phone: value }));
              }
            }}
          />
        </div>
      </div>

      {!googleSSORequired ? (
        <>
          <div className="border-t border-[#d3d3d3] pt-4 mt-2">
            <h4 className={`${USER_ACCESS_SUBCARD_TITLE} mb-4`}>
              {t("settingsUsers.passwordSection")}
            </h4>
            <div className="flex flex-col gap-4">
              <div className="waste-flow-form-field">
                <ProfileFieldLabel
                  htmlFor="user-password"
                  required={isCreate}
                  infoTip={
                    !isCreate
                      ? t("settingsUsers.passwordOptionalTip")
                      : undefined
                  }
                >
                  {t("New password")}
                </ProfileFieldLabel>
                <PasswordWithSuggestion
                  key={`password-suggest-${passwordResetKey}-wizard`}
                  value={data.password || ""}
                  showSuggestion={isCreate}
                  resetKey={passwordResetKey}
                  onChange={(e) => handleFormChange(e, data, setData)}
                  onApplySuggestion={handleApplyPasswordSuggestion}
                  inputClassName={USER_ACCESS_INPUT_CLASS}
                />
              </div>

              <div className="waste-flow-form-field">
                <ProfileFieldLabel
                  htmlFor="user-password-confirm"
                  required={isCreate}
                >
                  {t("Confirm password")}
                </ProfileFieldLabel>
                <PasswordConfirmField
                  value={data.passwordConfirm || ""}
                  onChange={(e) => handleFormChange(e, data, setData)}
                  inputClassName={USER_ACCESS_INPUT_CLASS}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <InfoHint>{t("settingsUsers.googleSsoHint")}</InfoHint>
      )}
    </WasteFlowFormPanel>
  );
};

/** Onglet / étape Sites. */
export const UserAccessSitesSection = ({
  sites = [],
  selectedSites = [],
  onAuthorizedAddressesChange,
  multiselectAddressRef,
}) => {
  const { t } = useTranslation();

  return (
    <WasteFlowFormPanel title={t("settingsUsers.stepSites")}>
      <div className="waste-flow-form-field">
        <ProfileFieldLabel
          htmlFor="user-sites"
          infoTip={t("settingsUsers.sitesTip")}
        >
          {t("settingsUsers.sitesFieldLabel")}
        </ProfileFieldLabel>
        <div className="user-access-wizard-multiselect" id="user-sites">
          <Multiselect
            options={sites}
            ref={multiselectAddressRef}
            selectedValues={selectedSites}
            placeholder={t("Choose my addresses")}
            displayValue="name"
            onSelect={(list) =>
              onAuthorizedAddressesChange(list.map((opt) => opt))
            }
            onRemove={(list) =>
              onAuthorizedAddressesChange(list.map((opt) => opt))
            }
            showArrow={true}
            hidePlaceholder={true}
            showCheckbox={true}
            closeOnSelect={false}
          />
        </div>
      </div>
    </WasteFlowFormPanel>
  );
};

/** Onglet / étape Notifications. */
export const UserAccessNotificationsSection = ({
  data,
  setData,
  showNotificationTest = false,
  showPushDiagnostics = false,
  onPushDiagnosticsUpdated,
}) => {
  const { t } = useTranslation();

  const sessionUserId = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null")?._id;
    } catch {
      return null;
    }
  })();

  const isSelf =
    Boolean(data?._id) &&
    Boolean(sessionUserId) &&
    String(data._id) === String(sessionUserId);

  return (
    <WasteFlowFormPanel title={t("settingsUsers.stepNotifications")}>
      <div className="user-access-wizard-notifications">
        <UserAccessNotificationMatrix data={data} setData={setData} />

        <UserAccessPushDeviceStatus
          data={data}
          setData={setData}
          isSelf={isSelf}
        />

        {showNotificationTest &&
        data?._id &&
        (isSelf || showPushDiagnostics) ? (
          <NotificationTestPanel userId={data._id} />
        ) : null}

        {showPushDiagnostics ? (
          <UserAccessPushDiagnostics
            data={data}
            onDiagnosticsUpdated={onPushDiagnosticsUpdated}
          />
        ) : null}
      </div>
    </WasteFlowFormPanel>
  );
};

/** Onglet / étape Rapports. */
export const UserAccessReportsSection = ({ data, setData }) => {
  const { t } = useTranslation();

  return (
    <WasteFlowFormPanel title={t("settingsUsers.stepReports")}>
      <div className="user-access-wizard-reports">
        <div className="zone-settings__checkbox-row user-access-wizard-daily-row">
          <div className="zone-settings__checkbox-wrap">
            <input
              type="checkbox"
              id="receiveDailyPerformanceReport"
              className="zone-settings__checkbox"
              name="receiveDailyPerformanceReport"
              checked={!!data.receiveDailyPerformanceReport}
              onChange={(e) => handleFormChange(e, data, setData)}
            />
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            <label
              htmlFor="receiveDailyPerformanceReport"
              className="zone-settings__checkbox-label mb-0 ml-3"
            >
              {t("receiveDailyPerformanceReport")}
            </label>
            <InfoTip
              nudgeY={1}
              widthMaxRem={20}
              icon={<ActivityMetricInfoIcon />}
            >
              {t("settingsUsers.dailyPerformanceReportHelp")}
            </InfoTip>
          </div>
        </div>
      </div>
    </WasteFlowFormPanel>
  );
};
